import { marked } from "marked"
import { useEffect, useState, type ChangeEvent, type FormEvent } from "react"
import DOMPurify from "dompurify"
import { useNavigate } from "react-router-dom"

import { Button } from "~/components/Button"
import { GlobalNav } from "~/components/GlobalNav"
import { NumericalInput } from "~/components/NumericalInput"
import { MarkdownContent } from "~/components/MarkdownContent"
import { useGameCreation } from "~/hooks/useGameCreation"
import { useAccount } from "~/hooks/useAccount"
import { useDagProgram } from "~/hooks/useDagProgram"
import { api } from "~/services/api"

interface InputProps {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  className?: string;
}

const Input = ({ value, onChange, placeholder, type = "text", className = "" }: InputProps) => (
  <input
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    className={`w-full px-3 py-2 text-white bg-[#212425] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#60e25e] focus:ring-opacity-50 ${className}`}
  />
);

// Local storage keys
const STORAGE_KEY = 'dag-game-creation-draft'

// Form data interface
interface FormData {
  game: {
    name: string;
    description: string;
    readme: string;
  };
  token: {
    icon: string;
    symbol: string;
    name: string;
    description: string;
  };
  distribution: {
    snipeAmount: number;
  };
  repository: {
    name: string;
    createType: "new" | "fork";
    sourceRepositoryUrl: string;
  };
}

// Helper functions for local storage
const saveToLocalStorage = (data: FormData) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save to local storage:', error);
  }
};

const loadFromLocalStorage = (): FormData | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Failed to load from local storage:', error);
    return null;
  }
};

const clearLocalStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear local storage:', error);
  }
};

export default function GameCreate() {
  return (
    <div className="">
      <GlobalNav />
      <div className="pt-6 px-10 max-w-screen-md mx-auto">
        <Panel />
      </div>
    </div>
  )
}

const Panel = () => {
  const navigate = useNavigate()
  const { 
    createGame, 
    retryDAGCreation,
    isLoading: isCreating, 
    isConfirming,
    isCreatingDAG,
    error: creationError, 
    transactionResult,
    clearError 
  } = useGameCreation()
  const { isAuthenticated, user } = useAccount()
  const { initializeProject } = useDagProgram()

  const [readmeMode, setReadmeMode] = useState<'edit' | 'preview'>('edit')
  const [hasDraft, setHasDraft] = useState(false)
  
  const [game, setGame] = useState({
    name: "",
    description: "",
    readme: "",
    readmeHtml: "",
  })
  const [token, setToken] = useState({
    icon: "",
    symbol: "",
    name: "",
    description: "",
  })
  const [distribution, setDistribution] = useState({
    snipeAmount: 0,
  })
  const [repository, setRepository] = useState({
    name: "",
    createType: "new" as "new" | "fork",
    sourceRepositoryUrl: "",
  })

  // Check for existing draft on component mount
  useEffect(() => {
    const draft = loadFromLocalStorage();
    if (draft) {
      setHasDraft(true);
    }
  }, []);

  // Save form data to local storage whenever it changes
  useEffect(() => {
    const formData: FormData = {
      game: {
        name: game.name,
        description: game.description,
        readme: game.readme,
      },
      token: {
        icon: token.icon,
        symbol: token.symbol,
        name: token.name,
        description: token.description,
      },
      distribution: {
        snipeAmount: distribution.snipeAmount,
      },
      repository: {
        name: repository.name,
        createType: repository.createType,
        sourceRepositoryUrl: repository.sourceRepositoryUrl,
      },
    };
    
    // Only save if there's actual data
    const hasData = Object.values(formData).some(section => 
      Object.values(section).some(value => 
        value !== "" && value !== 0 && value !== "new"
      )
    );
    
    if (hasData) {
      saveToLocalStorage(formData);
    }
  }, [game, token, distribution, repository]);

  useEffect(() => {
    // Only redirect if we're authenticated but don't have GitHub connected
    // Don't redirect if we're still loading or not authenticated yet
    if (isAuthenticated && user && !user.githubConnected) {
      navigate("/profile")
    }
  }, [isAuthenticated, user, navigate])

  const restoreDraft = () => {
    const draft = loadFromLocalStorage();
    if (draft) {
      setGame({
        name: draft.game.name,
        description: draft.game.description,
        readme: draft.game.readme,
        readmeHtml: "",
      });
      setToken({
        icon: draft.token.icon,
        symbol: draft.token.symbol,
        name: draft.token.name,
        description: draft.token.description,
      });
      setDistribution({
        snipeAmount: draft.distribution.snipeAmount,
      });
      setRepository({
        name: draft.repository.name,
        createType: draft.repository.createType,
        sourceRepositoryUrl: draft.repository.sourceRepositoryUrl,
      });
      setHasDraft(false);
      clearLocalStorage();
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!game.name || !token.symbol || !token.name || !token.icon || !repository.name) {
      alert("Please fill in all required fields")
      return
    }

    if (repository.createType === "fork" && !repository.sourceRepositoryUrl) {
      alert("Please provide a source repository URL when forking")
      return
    }

    try {
      clearError()
      console.log("Starting game creation...")
      console.log("Form data:", { game, token, repository, distribution })

      const result = await createGame({
        game: {
          name: game.name,
          description: game.description,
          readme: game.readme,
        },
        token: {
          name: token.name,
          symbol: token.symbol,
          description: token.description,
          icon: token.icon,
        },
        repository: {
          name: repository.name,
          createType: repository.createType,
          sourceRepositoryUrl: repository.sourceRepositoryUrl,
        },
        distribution: {
          snipeAmount: distribution.snipeAmount,
        },
      })

      console.log("Game created successfully:", result)
      alert(`Game created successfully!\nProject PDA: ${result.projectPda}\nTransaction: ${result.transactionSignature}\nDAG ID: ${result.dag.dagId}`)

      // Clear local storage on successful creation
      clearLocalStorage();
      setHasDraft(false);

      // Navigate to the newly created game using repository name
      navigate(`/games/${repository.name}`)
    } catch (err) {
      console.error('Failed to create game:', err)
      // Error is already handled by the useGameCreation hook
      // Keep the draft data in case user wants to retry
    }
  }

  const handleIconChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onloadend = () => {
      setToken({ ...token, icon: reader.result as string })
    }
    reader.readAsDataURL(file)
  }

  return (
    <div className="flex flex-col gap-8 p-6 rounded-lg border border-solid border-[#212425]">
      <div className="flex items-center justify-between self-stretch relative gap-2">
        <p className="text-2xl font-semibold text-left text-white">New Game</p>
        {hasDraft && (
          <Button 
            onClick={restoreDraft}
            type="secondary"
            size="sm"
          >
            Restore Draft
          </Button>
        )}
      </div>

      {/* Game Info */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center self-stretch relative gap-2">
          <p className="text-xl text-left text-white">Game Info</p>
        </div>
        <div className="flex flex-col gap-4 p-4 rounded-lg border border-solid border-[#212425]">
          <div className="flex flex-col gap-2">
            <p className="text-base font-medium text-left text-white">
              Name <span className="text-red-500">*</span>
            </p>
            <Input
              value={game.name}
              onChange={(e) => setGame({ ...game, name: e.target.value })}
              placeholder="Enter game name"
            />
          </div>
          {/* README section */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-4">
              <p className="text-base font-medium text-left text-white">README</p>
              <button 
                className="text-xs font-medium text-left text-white" 
                onClick={() => setReadmeMode(readmeMode === 'edit' ? 'preview' : 'edit')}
              >
                <span className={readmeMode === 'edit' ? 'text-[#60e25e]' : 'text-[#6b6d6e]'}>Edit</span> / <span className={readmeMode === 'preview' ? 'text-[#60e25e]' : 'text-[#6b6d6e]'}>Preview</span>
              </button>
            </div>
            <div>
              {readmeMode === 'edit' ? (
                <textarea
                  value={game.readme}
                  onChange={(e) => setGame({ ...game, readme: e.target.value })}
                  placeholder="Enter README content in markdown format"
                  className="w-full h-[300px] px-3 py-2 text-white bg-[#212425] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#60e25e] focus:ring-opacity-50"
                />
              ) : (
                <div className="p-4 rounded-lg bg-[#212425]/50">
                  <MarkdownContent content={game.readme} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Token Info */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center self-stretch relative gap-2">
          <p className="text-xl text-left text-white">Token Info</p>
        </div>
        <div className="flex flex-col gap-4 p-4 rounded-lg border border-solid border-[#212425]">
          {/* Logo upload first */}
          <div className="flex flex-col gap-2">
            <p className="text-base font-medium text-left text-white">
              Logo <span className="text-red-500">*</span>
            </p>
            <div className="relative flex items-center gap-4">
              <input
                type="file"
                accept="image/*"
                id="logo-upload"
                className="hidden"
                onChange={handleIconChange}
              />
              <label
                htmlFor="logo-upload"
                className="flex items-center justify-center w-12 h-12 rounded-full bg-[#212425] cursor-pointer hover:bg-[#212425]/80"
              >
                {token.icon ? (
                  <img
                    src={token.icon}
                    alt="Token logo"
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <svg
                    className="w-6 h-6 text-[#6b6d6e]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                )}
              </label>
              <span className="text-sm text-[#9c9e9e]">
                {token.icon ? "Change logo" : "Upload logo"}
              </span>
            </div>
          </div>
          {/* Symbol */}
          <div className="flex flex-col gap-2">
            <p className="text-base font-medium text-left text-white">
              Symbol <span className="text-red-500">*</span>
            </p>
            <Input
              value={token.symbol}
              onChange={(e) => setToken({ ...token, symbol: e.target.value })}
              placeholder="Enter token symbol"
            />
          </div>
          {/* Name */}
          <div className="flex flex-col gap-2">
            <p className="text-base font-medium text-left text-white">
              Name <span className="text-red-500">*</span>
            </p>
            <Input
              value={token.name}
              onChange={(e) => setToken({ ...token, name: e.target.value })}
              placeholder="Enter token name"
            />
          </div>
          {/* Description */}
          <div className="flex flex-col gap-2">
            <p className="text-base font-medium text-left text-white">
              Description
            </p>
            <Input
              value={token.description}
              onChange={(e) => setToken({ ...token, description: e.target.value })}
              placeholder="Enter token description"
            />
          </div>
        </div>
      </div>

      {/* Repository Info */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center self-stretch relative gap-2">
          <p className="text-xl text-left text-white">Repository Info</p>
        </div>
        <div className="flex flex-col gap-4 p-4 rounded-lg border border-solid border-[#212425]">
          <div className="flex flex-col gap-2">
            <p className="text-base font-medium text-left text-white">
              Repository Name <span className="text-red-500">*</span>
            </p>
            <Input
              value={repository.name}
              onChange={(e) => setRepository({ ...repository, name: e.target.value })}
              placeholder="Enter repository name"
            />
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-base font-medium text-left text-white">
              Create Type
            </p>
            <select
              value={repository.createType}
              onChange={(e) => setRepository({ ...repository, createType: e.target.value as "new" | "fork" })}
              className="px-4 py-3 rounded-lg bg-white/5 border border-solid border-[#212425] text-sm font-medium text-left text-white focus:outline-none focus:ring-2 focus:ring-[#60e25e] focus:ring-opacity-50"
            >
              <option value="new">New</option>
              <option value="fork">Fork</option>
            </select>
          </div>
          {repository.createType === "fork" && (
            <div className="flex flex-col gap-2">
              <p className="text-base font-medium text-left text-white">
                Source Repository URL <span className="text-red-500">*</span>
              </p>
              <Input
                value={repository.sourceRepositoryUrl}
                onChange={(e) => setRepository({ ...repository, sourceRepositoryUrl: e.target.value })}
                placeholder="Enter source repository URL"
              />
            </div>
          )}
        </div>
      </div>

      {/* Distribution */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center self-stretch relative gap-2">
          <p className="text-xl text-left text-white">Distribution</p>
        </div>
        <div className="flex flex-col gap-4 p-4 rounded-lg border border-solid border-[#212425]">
          <div className="flex flex-col gap-2">
            <p className="text-base font-medium text-left text-white">
              Snipe Amount
            </p>
            <NumericalInput
              value={distribution.snipeAmount}
              onUserInput={(value) => setDistribution({ ...distribution, snipeAmount: Number(value) })}
              className="px-4 py-3 rounded-lg bg-white/5 border border-solid border-[#212425] text-sm font-medium text-left text-white"
            />
          </div>
        </div>
      </div>

      {/* Error display */}
      {creationError && (
        <div className="px-4 py-3 rounded-lg bg-red-900/20 border border-solid border-red-500/50">
          <p className="text-sm font-medium text-red-400">{creationError}</p>
          {transactionResult && (
            <Button 
              onClick={async () => {
                try {
                  clearError()
                  const result = await retryDAGCreation({
                    game: {
                      name: game.name,
                      description: game.description,
                      readme: game.readme,
                    },
                    token: {
                      name: token.name,
                      symbol: token.symbol,
                      description: token.description,
                      icon: token.icon,
                    },
                    repository: {
                      name: repository.name,
                      createType: repository.createType,
                      sourceRepositoryUrl: repository.sourceRepositoryUrl,
                    },
                    distribution: {
                      snipeAmount: distribution.snipeAmount,
                    },
                  })
                  
                  console.log("DAG created successfully on retry:", result)
                  alert(`DAG created successfully!\nDAG ID: ${result.dag.dagId}`)
                  
                  // Clear local storage on successful creation
                  clearLocalStorage();
                  setHasDraft(false);
                  
                  // Navigate to the newly created game using repository name
                  navigate(`/games/${repository.name}`)
                } catch (err) {
                  console.error('Retry failed:', err)
                }
              }}
              type="secondary"
              size="sm"
              className="mt-2"
              loading={isCreatingDAG}
            >
              Retry DAG Creation
            </Button>
          )}
        </div>
      )}

      <Button 
        loading={isCreating} 
        onClick={handleSubmit}
        disabled={!isAuthenticated || isCreating}
      >
        Create Game
      </Button>
    </div>
  )
}
